/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import {
  BuilderUserContext,
  CommunicationEntryPoint,
  CommunicationListItem,
  MeetingDialogConfig,
  meetingDialogRoute,
} from '@gh/builder-shared';
import {
  BuilderPermission,
  ActivityDetails,
  CommunicationType,
  ContactType,
  SystemRoleBit,
} from '@gh/builder-shared-data';
import { queryFormMode, State } from '@gh/common-shared';
import { TranslationService } from '@gh/core-mls';
import { DialogService, FormMode, isCreateFormMode, isEditFormMode, yesAnswer, yesNoConfirmation } from '@gh/core-ui';
import { GridRowEvent } from '@gh/core-ui-grid';
import { combineLatest$ } from '@gh/rx';
import { filter$, map$ } from '@gh/rx/operators';
import { filter } from 'lodash';
import { Observable } from 'rxjs';
import { LeadDetailsCommandService } from '../lead-details-command.service';
import {
  LeadCommunicationListFilters,
  LeadDetailsState,
  LeadDetailsStateEventType,
  queryCommunicationListActivityFilter,
  queryCommunicationListDocFilter,
  queryCommunicationListEmailFilter,
  queryCommunicationListFilters,
  queryCommunicationListSmsFilter,
  queryCommunicationListWebsiteFilter,
  queryCommunications,
  queryLeadActiveSalesFolderId,
  queryLeadGDriveUrl,
  queryLeadId,
  queryLeadIsSalesFolderSelected,
  queryLeadIsUnsubscribed,
} from '../lead-details.state';
import { LeadDetailsDcFormGridDefinition } from './lead-details-communication-list-grid-definition.service';

@Component({
  selector: 'gh-lead-details-dc-form',
  template: require('./lead-details-communication-list.component.html'),
  styles: [require('./lead-details-communication-list.component.scss')],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [LeadDetailsDcFormGridDefinition],
})
export class LeadDetailsCommunicationListComponent implements OnInit {

  communications$: Observable<CommunicationListItem[]>;
  filters$: Observable<LeadCommunicationListFilters>;
  showSms$: Observable<boolean>;
  showEmail$: Observable<boolean>;
  showDoc$: Observable<boolean>;
  showActivity$: Observable<boolean>;
  showWebsite$: Observable<boolean>;
  hasSalesFolders: boolean;
  gDriveUrl$: Observable<string>;
  isUnsubscribed?: boolean;

  constructor(private translations: TranslationService,
              public gridDefinition: LeadDetailsDcFormGridDefinition,
              private state: State<LeadDetailsState>,
              private dialogService: DialogService,
              private userContext: BuilderUserContext,
              private commandService: LeadDetailsCommandService) {
  }

  get isCreateMode(): boolean {
    return isCreateFormMode(this.state.project(queryFormMode));
  }

  get isEditMode(): boolean {
    return isEditFormMode(this.state.project(queryFormMode));
  }

  ngOnInit(): void {
    this.filters$ = this.state.select(queryCommunicationListFilters);
    this.showSms$ = this.state.select(queryCommunicationListSmsFilter);
    this.showDoc$ = this.state.select(queryCommunicationListDocFilter);
    this.showActivity$ = this.state.select(queryCommunicationListActivityFilter);
    this.showEmail$ = this.state.select(queryCommunicationListEmailFilter);
    this.showWebsite$ = this.state.select(queryCommunicationListWebsiteFilter);
    this.state.select(queryLeadIsSalesFolderSelected).subscribe(() => this.hasSalesFolders = !this.state.value.salesFolders.isEmpty);
    this.gDriveUrl$ = this.state.select(queryLeadGDriveUrl);
    this.isUnsubscribed = this.state.project(queryLeadIsUnsubscribed);

    this.communications$ = combineLatest$(this.filters$, this.state.select(queryCommunications)).pipe(
      map$(([{ showEmail, showSms, showDoc, showWebsite, showActivity }, communications]) => {
        if (showEmail && showSms && showDoc && showWebsite && showActivity) {
          return communications;
        } else if (!showEmail && !showSms && !showDoc && !showWebsite  && !showActivity) {
          return [];
        } else {
          return filter(communications, (communication) => {
            if (showSms &&
              (communication.communicationType === CommunicationType.OutgoingSms
                || communication.communicationType === CommunicationType.IncomingSms)) {
              return true;
            } else if (showEmail && communication.communicationType === CommunicationType.Email) {
              return true;
            } else if (showDoc && communication.communicationType === CommunicationType.Doc) {
              return true;
            } else if (showWebsite && communication.communicationType === CommunicationType.Website) {
              return true;
            } else if (showActivity && (communication.communicationType === CommunicationType.SalesActivity
              || communication.communicationType === CommunicationType.JobActivity)) {
              return true;
            } else {
              return false;
            }
          });
        }
      }));
  }

  onRowClicked(event: GridRowEvent<CommunicationListItem>): void {
    const item = event.data;

    switch (item.communicationType) {
      case CommunicationType.Email:
        this.commandService.showEmail(item);
        break;
      case CommunicationType.OutgoingSms:
      case CommunicationType.IncomingSms:
        this.commandService.showSms(item);
        break;
      case CommunicationType.Doc:
        this.commandService.showDoc(item);
        break;
      case CommunicationType.Website:
        this.commandService.showWebsite(item);
        break;
      case CommunicationType.JobActivity:
        this.showActivityDialog(item, this.formModeForJobActivity(), CommunicationEntryPoint.Job);
        break;
      case CommunicationType.SalesMeeting:
        this.showActivityDialog(item, this.formModeForSalesActivity(), CommunicationEntryPoint.LeadCommunication);
        break;
      case CommunicationType.SalesActivity:
        this.showActivityDialog(item, this.formModeForSalesActivity(), CommunicationEntryPoint.LeadCommunication);
        break;
      case CommunicationType.ContactActivity:
        this.showActivityDialog(item, this.formModeForContactActivity(item), CommunicationEntryPoint.Contact);
        break;
      default:
        break;
    }

  }

  sendEmail(): void {
    this.commandService.sendEmail();
  }

  sendSms(): void {
    this.commandService.sendSms();
  }

  addActivity(): void {
    const sfId = this.state.project(queryLeadActiveSalesFolderId);
    const leadId = this.state.project(queryLeadId);
    this.dialogService.openWithInput(meetingDialogRoute, <MeetingDialogConfig>{
      entryPoint: CommunicationEntryPoint.LeadCommunication,
      salesFolderId: sfId.activeSalesFolderId,
      mode: FormMode.CreateMode,
      helpId: 'sales/meetings',
      leadId,
    }).afterClosed().pipe(filter$(Boolean)).subscribe((communicationListItem) => {
      this.state.mutate({ type: LeadDetailsStateEventType.CommunicationListItemAdd, communicationListItem });
    });
  }

  showActivityDialog(
    communicationListItem: CommunicationListItem,
    formMode: FormMode | boolean,
    entryPoint: CommunicationEntryPoint): void {
    const leadId = this.state.project(queryLeadId);
    if (!formMode) {
      this.dialogService.error('msg_denied_no_permissions');
      return;
    }
    const isDone = communicationListItem.isDone;
    this.dialogService.openWithInput(meetingDialogRoute, <MeetingDialogConfig>{
      entryPoint,
      contactId: entryPoint === CommunicationEntryPoint.Contact ? communicationListItem.contactId : undefined,
      jobId: entryPoint === CommunicationEntryPoint.Job ? communicationListItem.jobId : undefined,
      salesFolderId: entryPoint === CommunicationEntryPoint.LeadCommunication ? communicationListItem.salesFolderId : undefined,
      mode: formMode,
      meetingId: communicationListItem.communicationId,
      leadId,
    }).afterClosed().pipe(
      filter$(Boolean))
      .subscribe((activity: ActivityDetails) => {
        this.state.mutate({
                            type: LeadDetailsStateEventType.CommunicationListItemUpdate,
                            communicationListItem: activity });
        if (activity.isDone && !isDone) {
          this.dialogService.confirmation(yesNoConfirmation('msg_activity_marked_as_done')).pipe(
            filter$(yesAnswer))
            .subscribe(() => this.addActivity());
        }
      });
  }

  showSmsChange(value: any): void {
    this.commandService.setCommunicationListFilter({
      showSms: value.checked,
      showEmail: this.state.project(queryCommunicationListEmailFilter),
      showDoc: this.state.project(queryCommunicationListDocFilter),
      showActivity: this.state.project(queryCommunicationListActivityFilter),
      showWebsite: this.state.project(queryCommunicationListWebsiteFilter),
    });
  }

  showEmailChange(value: any): void {
    this.commandService.setCommunicationListFilter({
      showEmail: value.checked,
      showSms: this.state.project(queryCommunicationListSmsFilter),
      showDoc: this.state.project(queryCommunicationListDocFilter),
      showActivity: this.state.project(queryCommunicationListActivityFilter),
      showWebsite: this.state.project(queryCommunicationListWebsiteFilter),
    });
  }

  showDocChange(value: any): void {
    this.commandService.setCommunicationListFilter(
      {
        showEmail: this.state.project(queryCommunicationListEmailFilter),
        showSms: this.state.project(queryCommunicationListSmsFilter),
        showDoc: value.checked,
        showActivity: this.state.project(queryCommunicationListActivityFilter),
        showWebsite: this.state.project(queryCommunicationListWebsiteFilter),
      });
  }

  showWebsiteChange(value: any): void {
    this.commandService.setCommunicationListFilter(
      {
        showEmail: this.state.project(queryCommunicationListEmailFilter),
        showSms: this.state.project(queryCommunicationListSmsFilter),
        showDoc: this.state.project(queryCommunicationListDocFilter),
        showActivity: this.state.project(queryCommunicationListActivityFilter),
        showWebsite: value.checked,
   });
  }
  showActivityChange(value: any): void {
    this.commandService.setCommunicationListFilter(
      {
        showEmail: this.state.project(queryCommunicationListEmailFilter),
        showSms: this.state.project(queryCommunicationListSmsFilter),
        showDoc: this.state.project(queryCommunicationListDocFilter),
        showActivity:  value.checked,
        showWebsite: this.state.project(queryCommunicationListWebsiteFilter),
      });
  }
  private formModeForJobActivity(): FormMode | boolean {
    if (this.userContext.hasSystemRole(SystemRoleBit.Corporate)
      || this.userContext.hasSystemRole(SystemRoleBit.Master)
      || this.userContext.hasPermission(BuilderPermission.JobAdmin.JobDetails.EDIT)) {
      return FormMode.EditMode;
    }
    if (this.userContext.hasPermission(BuilderPermission.JobAdmin.JobDetails.VIEW)) {
      return  FormMode.ViewMode;
    }
    return false;
  }

  private formModeForSalesActivity(): FormMode | boolean {
    if (this.userContext.hasSystemRole(SystemRoleBit.Corporate)
      || this.userContext.hasSystemRole(SystemRoleBit.Master)
      || this.userContext.hasPermission(BuilderPermission.Sales.Leads.EDIT)) {
      return FormMode.EditMode;
    }
    if (this.userContext.hasPermission(BuilderPermission.Sales.Leads.VIEW)) {
      return  FormMode.ViewMode;
    }
    return false;
  }

  private formModeForContactActivity(item: CommunicationListItem): FormMode | boolean {
    if (this.userContext.hasSystemRole(SystemRoleBit.Corporate)
      || this.userContext.hasSystemRole(SystemRoleBit.Master)
      || (!!item.contactTypeCode
        && this.userContext.hasPermission(BuilderPermission.Contacts[ContactType[item.contactTypeCode]].CREATE_EDIT))) {
      return FormMode.EditMode;
    }
    if (!item.contactTypeCode) {
      return false;
    }
    if (this.userContext.hasPermission(BuilderPermission.Contacts[ContactType[item.contactTypeCode]].VIEW)) {
      return  FormMode.ViewMode;
    }
    return false;
  }
}
